import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Paper,
  IconButton,
  MenuItem,
  Collapse,
  Pagination,
} from "@mui/material";
import { Print, FileDownload, FilterAltOff, ExpandMore } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import MultiLineChart, { ChartSeries } from "../components/charts/MultiLineChart";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  cashFlowReportService,
  CashFlowResponse,
  CashFlowTypeItem,
  CashFlowFilters,
  CashFlowDetailItem,
} from "../services/reportsService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import MonthSelect from "../components/Inputs/MonthSelect";

// Fluxo de Caixa — entradas e saídas efetivadas no livro financeiro (regime de caixa).
// Transferências entre contas do mesmo titular, saldos e provisões ficam fora.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatCurrencyCompact = (value: number): string =>
  value >= 1000 ? `${(value / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : String(Math.round(value));

// Donut SVG no mesmo padrão dos gráficos do Dashboard (círculos com strokeDasharray)
const INFLOW_COLORS = ["#28a745", "#20c997", "#03B4C6", "#6610f2", "#adb5bd"];
const OUTFLOW_COLORS = ["#dc3545", "#fd7e14", "#f39c12", "#512E5F", "#e83e8c", "#6610f2", "#20c997", "#6c757d", "#adb5bd", "#FF6347"];

interface DonutSegment {
  percent: number;
  offset: number;
  color: string;
}

const buildDonutSegments = (items: CashFlowTypeItem[], total: number, palette: string[]): DonutSegment[] => {
  let accumulated = 0;
  return items.map((item, index) => {
    const percent = total > 0 ? (100 * item.value) / total : 0;
    const segment = {
      percent,
      offset: -accumulated,
      color: palette[index % palette.length],
    };
    accumulated += percent;
    return segment;
  });
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};


const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const actionButtonSx = {
  padding: "0.5rem 1rem",
  borderRadius: "4px",
  fontSize: "0.85rem",
  fontWeight: typography.fontWeight.semibold,
  textTransform: "none" as const,
  boxShadow: "none",
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
};

const actionButtonGraySx = {
  ...actionButtonSx,
  backgroundColor: "#6c757d",
  "&:hover": { backgroundColor: "#5a6268", boxShadow: "none" },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const summaryCardSx = {
  flex: "1 1 200px",
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 88,
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  py: 0.5,
  borderBottom: "1px solid #f1f3f5",
};

interface DonutCardProps {
  title: string;
  items: CashFlowTypeItem[];
  total: number;
  totalColor: string;
  palette: string[];
}

const DonutCard: React.FC<DonutCardProps> = ({ title, items, total, totalColor, palette }) => (
  <Box sx={{ ...sectionStyle, flex: "1 1 420px" }}>
    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0, mx: "auto" }}>
        <svg width="140" height="140" viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
          {buildDonutSegments(items, total, palette).map((segment, index) => (
            <circle
              key={index}
              cx="21"
              cy="21"
              r="15.915"
              fill="transparent"
              stroke={segment.color}
              strokeWidth="3"
              strokeDasharray={`${segment.percent} ${100 - segment.percent}`}
              strokeDashoffset={segment.offset}
              strokeLinecap="round"
            />
          ))}
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold", color: totalColor, fontSize: 16 }}>
            {formatCurrencyCompact(total)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Total
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 260 }}>
        {items.map((item, index) => (
          <Box key={item.name} sx={rowStyle}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: palette[index % palette.length],
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 14, flex: 1 }}>{item.name}</Typography>
            <Typography sx={{ fontSize: 13, width: 110, textAlign: "right", color: colors.textPrimary }}>
              {formatCurrency(item.value)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6c757d", width: 48, textAlign: "right" }}>
              {total > 0 ? ((100 * item.value) / total).toFixed(1) : "0.0"}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

/** Cabeçalho de bloco que abre/fecha o conteúdo (acordeão do dashboard). */
const SectionHeader: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  subtitle?: string;
}> = ({ title, open, onToggle, subtitle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      userSelect: "none",
      mb: open ? 0.5 : 0,
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
        {title}
      </Typography>
      {subtitle && open && (
        <Typography sx={{ fontSize: 12, color: "#6c757d", mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>
    <IconButton size="small" aria-label={open ? "Recolher" : "Expandir"} className="no-print">
      <ExpandMore
        fontSize="small"
        sx={{ transition: "transform 200ms", transform: open ? "rotate(180deg)" : "none" }}
      />
    </IconButton>
  </Box>
);

const ReportCashFlow: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const initialBounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState<string>(currentCompetence());
  const [fromDate, setFromDate] = useState<string>(initialBounds.from);
  const [toDate, setToDate] = useState<string>(initialBounds.to);
  const [data, setData] = useState<CashFlowResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // filtros da tela (search é aplicado com debounce; os demais, na hora)
  const [search, setSearch] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [subCategory, setSubCategory] = useState<string>("");
  const [operation, setOperation] = useState<string>("");
  const [account, setAccount] = useState<string>("");

  // lista de lançamentos (paginada, respeita os filtros)
  const [entries, setEntries] = useState<CashFlowDetailItem[]>([]);
  const [entriesTotal, setEntriesTotal] = useState<number>(0);
  const [entriesPage, setEntriesPage] = useState<number>(1);
  const [entriesLoading, setEntriesLoading] = useState<boolean>(false);
  const ENTRIES_PAGE_SIZE = 25;

  // acordeão dos blocos (aberto por padrão)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    balances: true,
    result: true,
    charts: true,
    entries: true,
  });
  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to ||
    Boolean(search || category || subCategory || operation || account);

  const loadData = useCallback(
    async (from: string, to: string, filters: CashFlowFilters) => {
      if (!from || !to) return;
      setLoading(true);
      setError("");
      try {
        const response = await cashFlowReportService.get(from, to, filters);
        setData(response);
      } catch {
        setError("Não foi possível carregar o fluxo de caixa. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadEntries = useCallback(
    async (from: string, to: string, filters: CashFlowFilters, page: number) => {
      if (!from || !to) return;
      setEntriesLoading(true);
      try {
        const response = await cashFlowReportService.details(
          from,
          to,
          page,
          ENTRIES_PAGE_SIZE,
          filters,
        );
        setEntries(response.items);
        setEntriesTotal(response.total);
      } catch {
        setEntries([]);
        setEntriesTotal(0);
      } finally {
        setEntriesLoading(false);
      }
    },
    [],
  );

  const currentFilters = useCallback(
    (): CashFlowFilters => ({ search, category, subCategory, operation, account }),
    [search, category, subCategory, operation, account],
  );

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
      setEntriesPage(1);
    }
  };

  const handleFromChange = (value: string) => {
    setFromDate(value);
    setCompetence("");
    setEntriesPage(1);
  };

  const handleToChange = (value: string) => {
    setToDate(value);
    setCompetence("");
    setEntriesPage(1);
  };

  // categoria manda na subcategoria: trocar a categoria zera a subcategoria
  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setSubCategory("");
    setEntriesPage(1);
  };

  const clearFilters = () => {
    const competenceNow = currentCompetence();
    const bounds = competenceBounds(competenceNow);
    setCompetence(competenceNow);
    setFromDate(bounds.from);
    setToDate(bounds.to);
    setSearch("");
    setCategory("");
    setSubCategory("");
    setOperation("");
    setAccount("");
    setEntriesPage(1);
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await cashFlowReportService.exportXlsx(fromDate, toDate, currentFilters());
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lancamentos_${fromDate}_a_${toDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const filters: CashFlowFilters = { search, category, subCategory, operation, account };
      loadData(fromDate, toDate, filters);
      loadEntries(fromDate, toDate, filters, entriesPage);
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [
    fromDate,
    toDate,
    search,
    category,
    subCategory,
    operation,
    account,
    entriesPage,
    loadData,
    loadEntries,
  ]);

  const hasMovement = data
    ? data.totals.totalInflow !== 0 || data.totals.totalOutflow !== 0
    : false;

  const maxExpense = data && data.topExpenses.length > 0 ? data.topExpenses[0].value : 0;

  const monthlySeries: ChartSeries[] = data
    ? [
        { name: "Entradas", color: "#28a745", values: data.monthly.map((m) => m.inflow) },
        { name: "Saídas", color: "#dc3545", values: data.monthly.map((m) => m.outflow) },
      ]
    : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 landscape; margin: 10mm; }
          /* a lista não pode ficar presa no scroll horizontal ao imprimir */
          .entries-scroll { overflow: visible !important; }
          .entries-scroll > * { min-width: 0 !important; width: 100% !important; }
          .MuiCollapse-root { height: auto !important; visibility: visible !important; overflow: visible !important; }
          .MuiCollapse-wrapper, .MuiCollapse-wrapperInner { height: auto !important; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            className="c4u-page-header c4u-page-header--card"
          >
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Fluxo de Caixa
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Entradas e saídas efetivadas no livro financeiro (regime de caixa). Transferências
                entre contas do mesmo titular, saldos e provisões não entram nos totais.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros e ações */}
          <Paper className="no-print" elevation={0} sx={filterCardSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.75rem",
                alignItems: { xs: "stretch", sm: "flex-start" },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: "0.75rem",
                  flex: 1,
                }}
              >
                <TextField
                  size="small"
                  label="Busca por palavra"
                  placeholder="Buscar na descrição"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setEntriesPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 60 }}
                />
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={handleCompetenceChange}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={fromDate}
                  onChange={(e) => handleFromChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={toDate}
                  onChange={(e) => handleToChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  size="small"
                  label="Categoria"
                  value={category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.filterOptions?.categories ?? []).map((c) => (
                    <MenuItem key={c.name} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Subcategoria"
                  value={subCategory}
                  onChange={(e) => {
                    setSubCategory(e.target.value);
                    setEntriesPage(1);
                  }}
                  disabled={!category}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  helperText={!category ? "Escolha a categoria" : undefined}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.filterOptions?.categories ?? [])
                    .find((c) => c.name === category)
                    ?.subCategories.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Operação"
                  value={operation}
                  onChange={(e) => {
                    setOperation(e.target.value);
                    setEntriesPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="Crédito">Crédito (entrada)</MenuItem>
                  <MenuItem value="Débito">Débito (saída)</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Conta"
                  value={account}
                  onChange={(e) => {
                    setAccount(e.target.value);
                    setEntriesPage(1);
                  }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.filterOptions?.accounts ?? []).map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <IconButton
                onClick={clearFilters}
                disabled={!hasFilterChanges || loading}
                title="Limpar filtros"
                sx={{ ...clearFilterButtonSx, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "auto" } }}
              >
                <FilterAltOff fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { sm: "flex-end" },
                flexWrap: { sm: "wrap" },
                gap: "0.5rem",
                // no celular cada ação ocupa a largura toda (alvo de toque confortável)
                "& .MuiButton-root": {
                  flexShrink: 0,
                  minHeight: 44,
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={loading || exporting || !data}
                sx={actionButtonGraySx}
              >
                {exporting ? "Exportando..." : "Exportar lançamentos"}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading || !data}
                sx={actionButtonSx}
              >
                Imprimir
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && !hasMovement && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhuma movimentação encontrada para o período selecionado.
            </Box>
          )}

          {!loading && !error && data && hasMovement && (
            <>
              {/* Cards de totais */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Entradas</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: "#28a745" }}>
                    {formatCurrency(data.totals.totalInflow)}
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Saídas</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: "#dc3545" }}>
                    {formatCurrency(data.totals.totalOutflow)}
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Resultado de caixa</Typography>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: "bold",
                      color: data.totals.netCash >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(data.totals.netCash)}
                  </Typography>
                </Box>
              </Box>

              {/* Saldos e conciliação de caixa */}
              {(() => {
                const rec = data.reconciliation;
                const line = (label: string, value: number, opts?: { bold?: boolean; sign?: boolean }) => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.5,
                      borderBottom: "1px solid #f1f3f5",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: opts?.bold ? 600 : 400, color: colors.textPrimary }}>
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: opts?.bold ? 600 : 400,
                        fontVariantNumeric: "tabular-nums",
                        color: opts?.sign ? (value >= 0 ? "#28a745" : "#dc3545") : colors.textPrimary,
                      }}
                    >
                      {formatCurrency(value)}
                    </Typography>
                  </Box>
                );
                return (
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}>
                    {data.currentBalances && (
                      <Box sx={{ ...sectionStyle, flex: "1 1 320px", mb: 0 }}>
                        <SectionHeader
                          title="Saldo por conta"
                          open={openSections.balances}
                          onToggle={() => toggleSection("balances")}
                          subtitle={`Posição apurada pelo livro financeiro em ${
                            data.currentBalances.accounts[0]?.balanceDate ?? "hoje"
                          } — independe do período filtrado.`}
                        />
                        <Collapse in={openSections.balances}>
                        {data.currentBalances.accounts.map((a) => (
                          <Box
                            key={a.name}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.4,
                              borderBottom: "1px solid #f1f3f5",
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                              {a.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontVariantNumeric: "tabular-nums",
                                color: a.balance < 0 ? "#dc3545" : colors.textPrimary,
                              }}
                            >
                              {formatCurrency(a.balance)}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.6, mt: 0.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Total</Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              fontVariantNumeric: "tabular-nums",
                              color: data.currentBalances.total < 0 ? "#dc3545" : "#28a745",
                            }}
                          >
                            {formatCurrency(data.currentBalances.total)}
                          </Typography>
                        </Box>
                        </Collapse>
                      </Box>
                    )}
                    <Box sx={{ ...sectionStyle, flex: "1 1 420px", mb: 0 }}>
                      <SectionHeader
                        title="Resultado do período"
                        open={openSections.result}
                        onToggle={() => toggleSection("result")}
                        subtitle="Entradas e saídas efetivadas no período (regime de caixa). A conferência compara o saldo que o livro produz com o saldo lançado do extrato: se não confere, há lançamento faltando ou duplicado."
                      />
                      <Collapse in={openSections.result}>
                      {line("(+) Entradas do período", data.totals.totalInflow)}
                      {line("(−) Saídas do período", data.totals.totalOutflow)}
                      {line("(=) Resultado de caixa", data.totals.netCash, { bold: true, sign: true })}
                      {rec.endingBalanceRecorded != null && (
                        <>
                          {line("Saldo das contas no fim do período", rec.endingBalanceRecorded, { sign: true })}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.5,
                              mt: 0.5,
                              borderTop: "1px solid #e9ecef",
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: "#6c757d" }}>Conferência do período</Typography>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: Math.abs(rec.difference ?? 0) < 0.01 ? "#28a745" : "#dc3545",
                              }}
                            >
                              {Math.abs(rec.difference ?? 0) < 0.01
                                ? "Confere — todos os lançamentos registrados"
                                : `Divergência de ${formatCurrency(rec.difference ?? 0)}`}
                            </Typography>
                          </Box>
                        </>
                      )}
                      </Collapse>
                    </Box>

                    {rec.excludedItems.length > 0 && (
                      <Box sx={{ ...sectionStyle, flex: "1 1 320px", mb: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                          Fora do resultado de caixa
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1 }}>
                          Não é movimento real do período — por isso não entra no resultado.
                        </Typography>
                        {rec.excludedItems.map((e) => (
                          <Box
                            key={e.name}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.5,
                              borderBottom: "1px solid #f1f3f5",
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>{e.name}</Typography>
                            <Typography sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                              {formatCurrency(e.value)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Donuts entradas x saídas */}
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <DonutCard
                  title="Entradas por tipo"
                  items={data.inflows}
                  total={data.totals.totalInflow}
                  totalColor="#28a745"
                  palette={INFLOW_COLORS}
                />
                <DonutCard
                  title="Saídas por tipo"
                  items={data.outflows}
                  total={data.totals.totalOutflow}
                  totalColor="#dc3545"
                  palette={OUTFLOW_COLORS}
                />
              </Box>

              {/* Top maiores gastos */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Top {data.topExpenses.length} maiores tipos de gasto
                </Typography>
                {data.topExpenses.map((item, index) => (
                  <Box key={item.name} sx={{ ...rowStyle, gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: "#6c757d", width: 22, textAlign: "right" }}>
                      {index + 1}.
                    </Typography>
                    <Typography sx={{ fontSize: 14, flex: "0 0 auto", minWidth: 180 }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ flex: 1, height: 8, background: "#f1f3f5", borderRadius: 4, overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: `${maxExpense > 0 ? (100 * item.value) / maxExpense : 0}%`,
                          height: "100%",
                          background: "#dc3545",
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 13, width: 110, textAlign: "right", color: colors.textPrimary }}>
                      {formatCurrency(item.value)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6c757d", width: 48, textAlign: "right" }}>
                      {data.totals.totalOutflow > 0
                        ? ((100 * item.value) / data.totals.totalOutflow).toFixed(1)
                        : "0.0"}%
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Evolução mensal (períodos com mais de um mês) */}
              {data.monthly.length > 1 && (
                <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                    Evolução mensal — entradas × saídas
                  </Typography>
                  <MultiLineChart
                    labels={data.monthly.map((m) => m.month)}
                    series={monthlySeries}
                    valueFormatter={formatCurrency}
                    showValues
                    labelFormatter={formatCurrencyCompact}
                  />
                </Box>
              )}

              {/* Lançamentos do período (respeita os filtros) */}
              <Box sx={sectionStyle}>
                <SectionHeader
                  title={`Lançamentos do período (${entriesTotal})`}
                  open={openSections.entries}
                  onToggle={() => toggleSection("entries")}
                  subtitle="Lista os lançamentos do livro financeiro conforme os filtros aplicados acima."
                />
                <Collapse in={openSections.entries}>
                  {entriesLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : entries.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "#6c757d", py: 2 }}>
                      Nenhum lançamento encontrado com os filtros aplicados.
                    </Typography>
                  ) : (
                    <>
                      <Box className="entries-scroll" sx={{ overflowX: "auto" }}>
                        <Box sx={{ minWidth: 980 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "96px minmax(220px, 1.6fr) minmax(200px, 1.2fr) minmax(170px, 1fr) 96px 130px",
                              gap: 1.5,
                              py: 0.75,
                              borderBottom: "2px solid #e9ecef",
                            }}
                          >
                            {["Data", "Descrição", "Categoria", "Conta", "Operação", "Valor"].map(
                              (header) => (
                                <Typography
                                  key={header}
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    textAlign: header === "Valor" ? "right" : "left",
                                  }}
                                >
                                  {header}
                                </Typography>
                              ),
                            )}
                          </Box>
                          {entries.map((entry, index) => (
                            <Box
                              key={`${entry.date}-${entry.description}-${entry.value}-${index}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "96px minmax(220px, 1.6fr) minmax(200px, 1.2fr) minmax(170px, 1fr) 96px 130px",
                                gap: 1.5,
                                py: 0.75,
                                borderBottom: "1px solid #f1f3f5",
                                alignItems: "center",
                              }}
                            >
                              <Typography sx={{ fontSize: 13 }}>{entry.date}</Typography>
                              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                                <Typography
                                  sx={{ fontSize: 13, overflowWrap: "anywhere" }}
                                  title={entry.notes || entry.description}
                                >
                                  {entry.description || "—"}
                                </Typography>
                                {entry.notes && (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "#adb5bd",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {entry.notes}
                                  </Typography>
                                )}
                              </Box>
                              <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere" }}>
                                {entry.subCategory
                                  ? `${entry.category} › ${entry.subCategory}`
                                  : entry.category || "—"}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere" }}>
                                {entry.account || "—"}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: "#6c757d" }}>
                                {entry.operationType}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  textAlign: "right",
                                  fontVariantNumeric: "tabular-nums",
                                  color:
                                    entry.operationType === "Crédito" ? "#28a745" : "#dc3545",
                                }}
                              >
                                {formatCurrency(entry.value)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      {entriesTotal > ENTRIES_PAGE_SIZE && (
                        <Box
                          className="no-print"
                          sx={{ display: "flex", justifyContent: "center", pt: 2 }}
                        >
                          <Pagination
                            size="small"
                            count={Math.ceil(entriesTotal / ENTRIES_PAGE_SIZE)}
                            page={entriesPage}
                            onChange={(_, value) => setEntriesPage(value)}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Collapse>
              </Box>

              {data.warnings.length > 0 && (
                <Box sx={{ ...sectionStyle, py: 1.5 }}>
                  {data.warnings.map((warning) => (
                    <Typography key={warning} sx={{ fontSize: 12, color: "#6c757d" }}>
                      • {warning}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportCashFlow;
